import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheckIcon, ClockIcon, MapPinIcon, MessageSquareIcon, StarIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { sellerFor } from '../../data/market';
import type { MarketItem, MarketPattern } from '../../types/market';
import { naira } from '../../utils/format';

/**
 * One card, but the facts it shows are chosen by the category's pattern — a
 * plate of food leads with its prep time, a used textbook with its condition and
 * course, a room with its distance from campus. Forcing them into one identical
 * listing row is what made the previous marketplace read as a document index.
 */

const ACTION_LABEL: Record<MarketPattern, string> = {
  ordering: 'Add to order',
  goods: 'Buy',
  property: 'View place',
  booking: 'Request',
  gig: 'Hire',
  discovery: 'Open'
};
interface ItemCardProps {
  item: MarketItem;
  pattern: MarketPattern;
  onAction?: (item: MarketItem) => void;
  /** Set when the card sits in a horizontal rail rather than a grid. */
  compact?: boolean;
}
export function ItemCard({
  item,
  pattern,
  onAction,
  compact
}: ItemCardProps) {
  const seller = sellerFor(item.sellerId);
  const soldOut = item.availability === 'Sold out' || item.availability === 'Closed now';
  return <article className={`flex flex-col overflow-hidden rounded-card border border-line bg-surface ${compact ? 'w-64 shrink-0' : ''}`} data-id="element-2839" data-mp="2jmon5uec3p8z">
      {item.imageUrl ? <div className="relative aspect-[4/3] w-full overflow-hidden bg-canvas" data-id="element-2840" data-mp="3c9j78q78omlx">
          <img src={item.imageUrl} alt={item.title} className="h-full w-full object-cover" loading="lazy" data-id="element-2841" data-mp="2xm6iksm4y0rb" />
          {item.tags?.[0] && <span className="absolute left-3 top-3 rounded-md bg-surface/95 px-2 py-1 text-caption font-semibold text-ink" data-id="element-2842" data-mp="3q9txbr0q5x61">
              {item.tags[0]}
            </span>}
        </div> : item.tags?.[0] && <div className="border-b border-line bg-canvas px-4 py-2" data-id="element-2843" data-mp="8r4wkswo3xl1">
            <span className="text-caption font-semibold text-ink-muted" data-id="element-2844" data-mp="3sae5s2m4b1g">{item.tags[0]}</span>
          </div>}

      <div className="flex flex-1 flex-col px-4 py-4" data-id="element-2845" data-mp="3uxa413bul1rn">
        <h3 className="text-body font-semibold text-ink" data-id="element-2846" data-mp="3qxjfl69smxqa">
          <Link to={`/market/item/${item.id}`} className="hover:text-brand-700" data-id="element-2847" data-mp="3qwzwhm8eq3ao">
            {item.title}
          </Link>
        </h3>

        {/* Price leads, because in commerce it is what the reader came for. */}
        {item.priceNaira !== undefined ? <p className="cz-num mt-1.5 text-h4 font-semibold text-ink" data-id="element-2848" data-mp="3c9eyu7uawwa5">
            {naira(item.priceNaira)}
            {item.priceUnit && item.priceUnit !== 'each' && <span className="ml-1 text-body-sm font-normal text-ink-muted" data-id="element-2849" data-mp="337ff37rwje3i">
                {item.priceUnit}
              </span>}
          </p> : <p className="mt-1.5 text-body font-semibold text-ink-muted" data-id="element-2850" data-mp="2yaoy8i89weh1">Quoted per job</p>}

        <p className="mt-2 text-body-sm text-ink-muted" data-id="element-2851" data-mp="1dso1624pcbfr">{item.summary}</p>

        {/* Category-specific facts. Each appears only where it means something. */}
        <ul className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-caption text-ink-muted" data-id="element-2852" data-mp="1msjeachtigif">
          {item.condition && <li data-id="element-2853" data-mp="1rpjjkl6uff8h">Condition: {item.condition}</li>}
          {item.courseCodes && item.courseCodes.length > 0 && <li className="cz-num" data-id="element-2854" data-mp="uxz9gh5ynxij">For {item.courseCodes.join(', ')}</li>}
          {item.prepMinutes !== undefined && <li className="inline-flex items-center gap-1" data-id="element-2855" data-mp="1408guy5xnarp">
              <ClockIcon aria-hidden className="h-4 w-4" data-id="element-2856" data-mp="13ynmp9nj5tud" />
              {item.prepMinutes} min
            </li>}
          {item.distanceKm !== undefined && <li className="cz-num inline-flex items-center gap-1" data-id="element-2857" data-mp="bbba9sb332es">
              <MapPinIcon aria-hidden className="h-4 w-4" data-id="element-2858" data-mp="gant81yu3pc3" />
              {item.distanceKm} km from campus
            </li>}
          {item.area && <li data-id="element-2859" data-mp="2iy2w6sxws6er">{item.area}</li>}
          {item.deliveryLabel && <li data-id="element-2860" data-mp="2dzj9urfekslv">{item.deliveryLabel}</li>}
        </ul>

        {/* Seller identity and trust, kept separate from the rating. */}
        {seller && <div className="mt-3 border-t border-line pt-3" data-id="element-2861" data-mp="2rs98pwg15s0e">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-ink" data-id="element-2862" data-mp="2myddv2v2cl9g">
              <Link to={`/market/provider/${seller.id}`} className="font-medium hover:text-brand-700" data-id="element-2863" data-mp="31nj8ediyhnb0">
                {seller.displayName}
              </Link>
              <span className="text-ink-subtle" data-id="element-2864" data-mp="3anzd1qbfr0hg">{seller.kind}</span>
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2" data-id="element-2865" data-mp="36n5ocuq65hni">
              {seller.verified ? <Badge tone="ok" data-id="element-2866" data-mp="1gc129jxh9wjg">
                  <BadgeCheckIcon aria-hidden className="h-4 w-4" data-id="element-2867" data-mp="1gb9hz59bzl8h" />
                  Verified provider
                </Badge> : <Badge tone="warn" data-id="element-2868" data-mp="124xtemblcogv">Not inspected by Campuzon</Badge>}
              {item.itemVerified !== undefined && item.category === 'accommodation' && <Badge tone={item.itemVerified ? 'ok' : 'warn'} data-id="element-2869" data-mp="sfnzd0x6svq4">
                  {item.itemVerified ? 'Property inspected' : 'Property not inspected'}
                </Badge>}
              {item.rating !== undefined && <span className="cz-num inline-flex items-center gap-1 text-caption text-ink-muted" data-id="element-2870" data-mp="igf9aj5g0191">
                  <StarIcon aria-hidden className="h-4 w-4 text-warn-700" data-id="element-2871" data-mp="nhmfidrxssdl" />
                  {item.rating.toFixed(1)}
                  {item.reviewCount !== undefined && ` (${item.reviewCount})`}
                </span>}
            </div>
          </div>}

        <div className="mt-4 flex flex-wrap items-center gap-2 pt-1" data-id="element-2872" data-mp="23u9xrr0lc4e6">
          {onAction ? <Button variant="primary" size="sm" disabled={soldOut} onClick={() => onAction(item)} data-id="element-2873" data-mp="28uftbi9zeb1j">
              {soldOut ? item.availability : ACTION_LABEL[pattern]}
            </Button> : soldOut ? <Button variant="primary" size="sm" disabled data-id="element-2874" data-mp="22de940psmh1q">
              {item.availability}
            </Button> : <Link to={`/market/item/${item.id}`} className="inline-flex min-h-touch items-center justify-center gap-1.5 rounded-md border border-brand-700 bg-brand-700 px-3.5 py-2 text-body-sm font-medium text-white transition-colors duration-150 ease-out hover:border-brand-800 hover:bg-brand-800 hover:text-white" data-id="element-2875" data-mp="1xf2z9pzi06ji">
              {ACTION_LABEL[pattern]}
            </Link>}
          <Button variant="ghost" size="sm" icon={<MessageSquareIcon className="h-4 w-4" data-id="element-2877" data-mp="2bdsv72al006a" />} data-id="element-2876" data-mp="2gb2j2dibo3s0">
            Message
          </Button>
          {item.availability === 'Limited' && !soldOut && <span className="text-caption text-warn-800" data-id="element-2878" data-mp="342q2699ceajq">Limited</span>}
        </div>
      </div>
    </article>;
}